import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { dataService } from '@/services/data-service';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { useRegisterPageRefresh } from '@/contexts/PageRefreshContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Search, Download, FileSpreadsheet, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Zap, Filter, Columns, FileText, FileX, ArrowRight } from 'lucide-react';
import { PageWithRightPanel } from '@/components/layout/PageWithRightPanel';
import { RightPanelBase, type RightPanelTab } from '@/components/RightPanel/RightPanelBase';
import { InsuranceValueFiltersTab } from '@/components/RightPanel/InsuranceValueFiltersTab';
import { ColumnsTab } from '@/components/RightPanel/ColumnsTab';
import { format } from 'date-fns';
import { exportToCSV, exportToExcel, exportToPDF, generateFilename, formatDateForExport, formatCurrencyForExport, type ExportColumn } from '@/lib/export-utils';
import { toast } from 'sonner';
import { useColumnResize } from '@/hooks/use-column-resize';
import { cn } from '@/lib/utils';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { ASSET_STATUS_LABELS } from '@/types/asset';

// Insurance Value Report Data
interface InsuranceValueData {
  id: string;
  assetCode: string;
  description: string;
  serialNumber?: string;
  endOfYearNBV: number;
  purchaseValue: number;
  purchaseDate: string;
  insuranceValue: number;
  category: string;
  location: string;
  status: string;
}

// Default columns configuration
const DEFAULT_COLUMNS = [
  { id: 'assetCode', label: 'Asset Code', visible: true },
  { id: 'description', label: 'Description', visible: true },
  { id: 'serialNumber', label: 'Serial Number', visible: true },
  { id: 'category', label: 'Category', visible: true },
  { id: 'location', label: 'Location', visible: true },
  { id: 'purchaseDate', label: 'Purchase Date', visible: true },
  { id: 'purchaseValue', label: 'Purchase Value', visible: true },
  { id: 'endOfYearNBV', label: 'Net Book Value', visible: true },
  { id: 'insuranceValue', label: 'Insurance Value', visible: true },
  { id: 'status', label: 'Status', visible: false },
];

interface SortIconProps {
  readonly columnKey: string;
  readonly sortConfig: { key: string | null; direction: 'asc' | 'desc' | null };
}

function SortIcon({ columnKey, sortConfig }: Readonly<SortIconProps>) {
  if (sortConfig.key !== columnKey) {
    return null;
  }
  return sortConfig.direction === 'asc' ? (
    <ArrowUp className="ml-2 h-4 w-4" />
  ) : (
    <ArrowDown className="ml-2 h-4 w-4" />
  );
}

export function InsuranceValueReport(): React.ReactElement {
  const navigate = useNavigate();
  const [data, setData] = useState<InsuranceValueData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Right Panel Context
  const {
    setIsExpanded,
    setActiveTab,
    selectedRowIds,
    setSelectedRowIds,
    visibleColumns: contextVisibleColumns,
    setVisibleColumns,
    columnOrder: contextColumnOrder,
    setColumnOrder,
    tableFilters,
    setTableFilters,
    setPendingFilters,
    isPinned,
    isApplyingFilters
  } = useRightPanel();

  // Column resize hook
  const initialColumnWidths = {
    select: 80,
    assetCode: 150,
    description: 300,
    serialNumber: 150,
    category: 180,
    location: 150,
    purchaseDate: 120,
    purchaseValue: 150,
    endOfYearNBV: 150,
    insuranceValue: 150,
    status: 120,
  };

  const { columnWidths, handleMouseDown } = useColumnResize(initialColumnWidths, ['select']);

  // Initialize visible columns from context or defaults
  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    if (!isInitialized) {
      if (contextVisibleColumns.length === 0) {
        const defaultVisible = DEFAULT_COLUMNS
          .filter(col => col.visible)
          .map(col => col.id);
        setVisibleColumns(defaultVisible);
      }
      if (contextColumnOrder.length === 0) {
        setColumnOrder(DEFAULT_COLUMNS.map(col => col.id));
      }
      
      // Initialize filters - show only Open and Part Disposed assets
      setTableFilters({ status: ['active', 'outOfService'] });
      setPendingFilters({
        selectedCategories: [],
        selectedStatuses: ['active', 'outOfService'],
        selectedLocations: [],
        selectedTypes: [],
        selectedTransactionTypes: [],
        dateRange: { from: undefined, to: undefined },
        transactionDateRange: { from: undefined, to: undefined },
        amountMin: '',
        amountMax: '',
        transactionAmountMin: undefined,
        transactionAmountMax: undefined,
        selectedFiscalPeriods: [],
        selectedCreatedBy: [],
        selectedSubCategories: [],
        selectedSubLocations: [],
        acquisitionDateRange: { from: undefined, to: undefined },
        acquisitionCostMin: '',
        acquisitionCostMax: '',
        netBookValueMin: '',
        netBookValueMax: '',
        selectedAssignedTo: [],
        selectedDepreciationMethods: [],
        warrantyExpiryDateRange: { from: undefined, to: undefined },
        selectedManufacturers: [],
      });
      
      setIsInitialized(true);
    }
  }, [isInitialized, contextVisibleColumns.length, contextColumnOrder.length, setVisibleColumns, setColumnOrder, setPendingFilters, setTableFilters]);

  // Use context columns or fall back to defaults
  const visibleColumns = contextVisibleColumns.length > 0 
    ? contextVisibleColumns 
    : DEFAULT_COLUMNS.filter(col => col.visible).map(col => col.id);

  const columnOrder = contextColumnOrder.length > 0
    ? contextColumnOrder
    : DEFAULT_COLUMNS.map(col => col.id);

  // Build ordered visible columns for rendering
  const orderedVisibleColumns = columnOrder
    .filter(id => visibleColumns.includes(id))
    .map(id => DEFAULT_COLUMNS.find(col => col.id === id))
    .filter(Boolean) as typeof DEFAULT_COLUMNS;

  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc' | null;
  }>({
    key: 'assetCode',
    direction: 'asc',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;

  const [totalPurchaseValue, setTotalPurchaseValue] = useState(0);
  const [totalNBV, setTotalNBV] = useState(0);
  const [totalInsuranceValue, setTotalInsuranceValue] = useState(0);
  const [totalAssets, setTotalAssets] = useState(0);

  // Load data function using the dedicated insurance-value report endpoint
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setData([]);

      const categoryFilters = tableFilters.category as string[] | undefined;
      const locationFilters = tableFilters.location as string[] | undefined;

      const result = await dataService.getInsuranceValueReport({
        search: searchQuery || undefined,
        sortBy: sortConfig.key || undefined,
        order: sortConfig.direction || undefined,
        category: categoryFilters?.length === 1 ? categoryFilters[0] : undefined,
        location: locationFilters?.length === 1 ? locationFilters[0] : undefined,
      });

      setData(result.items);
      setTotalPurchaseValue(result.totalPurchaseValue);
      setTotalNBV(result.totalNetBookValue);
      setTotalInsuranceValue(result.totalInsuranceValue);
      setTotalAssets(result.totalAssets);
    } catch (err) {
      console.error('Failed to load insurance value data:', err);
      setError('Failed to load insurance value data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, sortConfig.key, sortConfig.direction, tableFilters.category, tableFilters.location]);

  // Load data on mount and when mode changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Register refresh handler for breadcrumb
  useRegisterPageRefresh(loadData, '/reports/insurance-value');

  // Apply client-side filters for multi-select and range filters not supported by the backend
  const filteredData = data.filter((item) => {
    const categoryFilters = tableFilters.category as string[] | undefined;
    if (categoryFilters && categoryFilters.length > 1) {
      if (!categoryFilters.includes(item.category)) return false;
    }

    const locationFilters = tableFilters.location as string[] | undefined;
    if (locationFilters && locationFilters.length > 1) {
      if (!locationFilters.includes(item.location)) return false;
    }

    if (tableFilters.status && (tableFilters.status as string[]).length > 0) {
      if (!(tableFilters.status as string[]).includes(item.status)) return false;
    }

    if (tableFilters.acquisitionDateRange) {
      const itemDate = new Date(item.purchaseDate);
      if (tableFilters.acquisitionDateRange.from) {
        const fromDate = new Date(tableFilters.acquisitionDateRange.from);
        if (itemDate < fromDate) return false;
      }
      if (tableFilters.acquisitionDateRange.to) {
        const toDate = new Date(tableFilters.acquisitionDateRange.to);
        if (itemDate > toDate) return false;
      }
    }

    if (tableFilters.amountMin !== undefined) {
      const min = parseFloat(String(tableFilters.amountMin));
      if (!isNaN(min) && item.insuranceValue < min) return false;
    }
    if (tableFilters.amountMax !== undefined) {
      const max = parseFloat(String(tableFilters.amountMax));
      if (!isNaN(max) && item.insuranceValue > max) return false;
    }

    return true;
  });

  const formatCurrency = (value: number) => {
    return value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Sort handler - triggers server-side re-fetch via loadData dependency
  const handleSort = (key: string) => {
    setSortConfig((prevConfig) => {
      if (prevConfig.key !== key) {
        return { key, direction: 'asc' };
      }
      if (prevConfig.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      if (prevConfig.direction === 'desc') {
        return { key: null, direction: null };
      }
      return { key, direction: 'asc' };
    });
  };

  // Data is already sorted server-side; client-side filtering preserves that order
  const sortedData = filteredData;

  // Pagination calculations
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  // Reset to page 1 when data changes (triggered by server re-fetch or client-side filter changes)
  useEffect(() => {
    setCurrentPage(1);
  }, [data, tableFilters.status, tableFilters.acquisitionDateRange, tableFilters.amountMin, tableFilters.amountMax]);

  // Row selection handler
  const handleSelectionChange = useCallback((rowId: string, checked: boolean) => {
    setSelectedRowIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(rowId);
        if (newSet.size === 1 && !isPinned) {
          setIsExpanded(true);
          setActiveTab('actions');
        }
      } else {
        newSet.delete(rowId);
        if (newSet.size === 0 && !isPinned) {
          setIsExpanded(false);
        }
      }
      return newSet;
    });
  }, [setIsExpanded, setActiveTab, setSelectedRowIds, isPinned]);

  // Select all handler
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const allIds = new Set(paginatedData.map(item => item.id));
      setSelectedRowIds(allIds);
      if (allIds.size > 0 && !isPinned) {
        setIsExpanded(true);
        setActiveTab('actions');
      }
    } else {
      setSelectedRowIds(new Set());
      if (!isPinned) {
        setIsExpanded(false);
      }
    }
  }, [paginatedData, setSelectedRowIds, setIsExpanded, setActiveTab, isPinned]);

  const handleExportCSV = () => {
    try {
      const columns: ExportColumn[] = [];
      
      orderedVisibleColumns.forEach(column => {
        switch (column.id) {
          case 'assetCode':
            columns.push({ key: 'assetCode', label: 'Asset Code' });
            break;
          case 'description':
            columns.push({ key: 'description', label: 'Description' });
            break;
          case 'serialNumber':
            columns.push({ key: 'serialNumber', label: 'Serial Number' });
            break;
          case 'category':
            columns.push({ key: 'category', label: 'Category' });
            break;
          case 'location':
            columns.push({ key: 'location', label: 'Location' });
            break;
          case 'purchaseDate':
            columns.push({ key: 'purchaseDate', label: 'Purchase Date', format: formatDateForExport });
            break;
          case 'purchaseValue':
            columns.push({ key: 'purchaseValue', label: 'Purchase Value', format: formatCurrencyForExport });
            break;
          case 'endOfYearNBV':
            columns.push({ key: 'endOfYearNBV', label: 'Net Book Value', format: formatCurrencyForExport });
            break;
          case 'insuranceValue':
            columns.push({ key: 'insuranceValue', label: 'Insurance Value', format: formatCurrencyForExport });
            break;
          case 'status':
            columns.push({ key: 'status', label: 'Status' });
            break;
        }
      });

      exportToCSV({
        filename: generateFilename('insurance-value-report', 'csv'),
        columns,
        data: sortedData,
      });

      toast.success(`Exported ${sortedData.length} record(s) to CSV`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const handleExportExcel = () => {
    try {
      const columns: ExportColumn[] = [];
      
      orderedVisibleColumns.forEach(column => {
        switch (column.id) {
          case 'assetCode':
            columns.push({ key: 'assetCode', label: 'Asset Code' });
            break;
          case 'description':
            columns.push({ key: 'description', label: 'Description' });
            break;
          case 'serialNumber':
            columns.push({ key: 'serialNumber', label: 'Serial Number' });
            break;
          case 'category':
            columns.push({ key: 'category', label: 'Category' });
            break;
          case 'location':
            columns.push({ key: 'location', label: 'Location' });
            break;
          case 'purchaseDate':
            columns.push({ key: 'purchaseDate', label: 'Purchase Date', format: formatDateForExport });
            break;
          case 'purchaseValue':
            columns.push({ key: 'purchaseValue', label: 'Purchase Value', format: formatCurrencyForExport });
            break;
          case 'endOfYearNBV':
            columns.push({ key: 'endOfYearNBV', label: 'Net Book Value', format: formatCurrencyForExport });
            break;
          case 'insuranceValue':
            columns.push({ key: 'insuranceValue', label: 'Insurance Value', format: formatCurrencyForExport });
            break;
          case 'status':
            columns.push({ key: 'status', label: 'Status' });
            break;
        }
      });

      exportToExcel({
        filename: generateFilename('insurance-value-report', 'xls'),
        sheetName: 'Insurance Value',
        columns,
        data: sortedData,
      });

      toast.success(`Exported ${sortedData.length} record(s) to Excel`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const handleExportPDF = () => {
    try {
      const columns: ExportColumn[] = [];
      
      orderedVisibleColumns.forEach(column => {
        switch (column.id) {
          case 'assetCode':
            columns.push({ key: 'assetCode', label: 'Asset Code' });
            break;
          case 'description':
            columns.push({ key: 'description', label: 'Description' });
            break;
          case 'serialNumber':
            columns.push({ key: 'serialNumber', label: 'Serial Number' });
            break;
          case 'category':
            columns.push({ key: 'category', label: 'Category' });
            break;
          case 'location':
            columns.push({ key: 'location', label: 'Location' });
            break;
          case 'purchaseDate':
            columns.push({ key: 'purchaseDate', label: 'Purchase Date', format: formatDateForExport });
            break;
          case 'purchaseValue':
            columns.push({ key: 'purchaseValue', label: 'Purchase Value', format: formatCurrencyForExport });
            break;
          case 'endOfYearNBV':
            columns.push({ key: 'endOfYearNBV', label: 'Net Book Value', format: formatCurrencyForExport });
            break;
          case 'insuranceValue':
            columns.push({ key: 'insuranceValue', label: 'Insurance Value', format: formatCurrencyForExport });
            break;
          case 'status':
            columns.push({ key: 'status', label: 'Status' });
            break;
        }
      });

      // Build filters for PDF display
      const appliedFilters: Record<string, string> = {};
      if (tableFilters.category?.length) {
        appliedFilters.category = tableFilters.category.join(', ');
      }
      if (tableFilters.status?.length) {
        appliedFilters.status = tableFilters.status.join(', ');
      }
      if (tableFilters.location?.length) {
        appliedFilters.location = tableFilters.location.join(', ');
      }

      exportToPDF({
        filename: generateFilename('insurance-value-report', 'pdf'),
        title: 'Insurance Value Report',
        subtitle: 'Asset Management System',
        columns,
        data: sortedData,
        filters: appliedFilters,
        orientation: 'auto',
      });

      toast.success(`Exported ${sortedData.length} record(s) to PDF`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  // Helper function to render table cell content
  const renderCellContent = (columnId: string, item: InsuranceValueData) => {
    switch (columnId) {
      case 'assetCode':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3">
            <button
              onClick={() => navigate(`/assets/${item.id}`)}
              className="text-primary hover:text-primary/80 font-medium hover:underline hover:underline-offset-4 transition-all cursor-pointer inline-flex items-center gap-1 group"
            >
              {item.assetCode}
              <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span>
            </button>
          </TableCell>
        );
      case 'description':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3">
            <button
              onClick={() => navigate(`/assets/${item.id}`)}
              className="text-primary hover:text-primary/80 font-medium hover:underline hover:underline-offset-4 transition-all cursor-pointer inline-flex items-center gap-1 group"
            >
              {item.description}
              <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span>
            </button>
          </TableCell>
        );
      case 'serialNumber':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">{item.serialNumber || '-'}</TableCell>;
      case 'category':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">{item.category}</TableCell>;
      case 'location':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">{item.location}</TableCell>;
      case 'purchaseDate':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300 tabular-nums">{format(new Date(item.purchaseDate), 'dd/MM/yyyy')}</TableCell>;
      case 'purchaseValue':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums">{formatCurrency(item.purchaseValue)}</TableCell>;
      case 'endOfYearNBV':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums">{formatCurrency(item.endOfYearNBV)}</TableCell>;
      case 'insuranceValue':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums font-semibold">{formatCurrency(item.insuranceValue)}</TableCell>;
      case 'status':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">{ASSET_STATUS_LABELS[item.status as keyof typeof ASSET_STATUS_LABELS] || item.status}</TableCell>;
      default:
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">-</TableCell>;
    }
  };

  // Action Card Component
  const ActionCard = ({
    icon: Icon,
    title,
    description,
    onClick,
    variant,
    disabled
  }: {
    icon: React.ElementType;
    title: string;
    description: string;
    onClick?: () => void;
    variant: 'view' | 'action' | 'delete';
    disabled?: boolean;
  }) => {
    const variantStyles = {
      view: {
        container: 'bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950/50',
        icon: 'text-blue-600 dark:text-blue-400',
        iconBg: 'bg-blue-100 dark:bg-blue-900/50'
      },
      action: {
        container: 'bg-primary/5 dark:bg-primary/10 hover:bg-primary/10 dark:hover:bg-primary/15',
        icon: 'text-primary dark:text-primary',
        iconBg: 'bg-primary/10 dark:bg-primary/20'
      },
      delete: {
        container: 'bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50',
        icon: 'text-red-600 dark:text-red-400',
        iconBg: 'bg-red-100 dark:bg-red-900/50'
      }
    };

    const styles = variantStyles[variant];

    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'w-full p-[5px] rounded-lg transition-all duration-200 text-left group cursor-pointer',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          styles.container
        )}
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110',
            styles.iconBg
          )}>
            <Icon className={cn('h-6 w-6', styles.icon)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold text-sm text-foreground">
                {title}
              </h3>
              <ArrowRight className={cn('h-4 w-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity', styles.icon)} />
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {description}
            </p>
          </div>
        </div>
      </button>
    );
  };

  const CustomActionsTab = () => {
    const hasSelection = selectedRowIds.size > 0;

    return (
      <div className="p-4 space-y-3">
        {/* Export All Section */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
            Export
          </h4>
          <ActionCard
            icon={Download}
            title="Export to CSV"
            description="Download all insurance values as CSV file"
            variant="action"
            onClick={handleExportCSV}
          />
          <ActionCard
            icon={FileSpreadsheet}
            title="Export to Excel"
            description="Generate Excel spreadsheet with all data"
            variant="action"
            onClick={handleExportExcel}
          />
          <ActionCard
            icon={FileText}
            title="Export to PDF"
            description="Generate professional PDF report"
            variant="action"
            onClick={handleExportPDF}
          />
        </div>

        {!hasSelection ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center border-t pt-4">
            <p className="text-xs text-muted-foreground">
              Select one or more assets to see additional actions
            </p>
          </div>
        ) : (
          <div className="space-y-2 border-t pt-3">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
              Export Selected
            </h4>
            <ActionCard
              icon={Download}
              title="Export Selected to CSV"
              description={`Download ${selectedRowIds.size} asset${selectedRowIds.size !== 1 ? 's' : ''} as CSV`}
              variant="action"
              onClick={() => console.log('Export selected to CSV')}
            />
            <ActionCard
              icon={FileSpreadsheet}
              title="Export Selected to Excel"
              description={`Generate Excel file with ${selectedRowIds.size} asset${selectedRowIds.size !== 1 ? 's' : ''}`}
              variant="action"
              onClick={() => console.log('Export selected to Excel')}
            />
          </div>
        )}
      </div>
    );
  };

  // Calculate visible columns count for badge
  const visibleColumnsForBadge = contextVisibleColumns.length > 0 
    ? contextVisibleColumns 
    : DEFAULT_COLUMNS.filter(col => col.visible).map(col => col.id);

  // Define page-specific tabs for the right panel
  const rightPanelTabs: RightPanelTab[] = [
    {
      id: 'actions',
      label: 'Actions',
      icon: Zap,
      content: <CustomActionsTab />,
      showBadge: true,
    },
    {
      id: 'filters',
      label: 'Filters',
      icon: Filter,
      content: <InsuranceValueFiltersTab />,
    },
    {
      id: 'columns',
      label: 'Columns',
      icon: Columns,
      content: <ColumnsTab />,
      badgeCount: visibleColumnsForBadge.length,
    },
  ];


  return (
    <PageWithRightPanel>
      <div className="flex h-full w-full flex-col overflow-hidden">
        {/* Page Header */}
        <div className="border-b bg-card p-6 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <FileText className="h-6 w-6 text-primary" />
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Insurance Value Report</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Asset values for insurance purposes
                </p>
              </div>
            </div>
            
            {/* Search Bar */}
            <div className="flex items-center gap-3">
              <EvoFeatureFeedbackCollector feature="Insurance Value Report" />
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search assets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {(loading || isApplyingFilters) && (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <p className="text-lg font-medium">Loading insurance values...</p>
              </div>
            </div>
          )}
          {!loading && error && (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <p className="text-lg font-medium text-destructive">Error</p>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
            </div>
          )}
          {!loading && !error && (
            <div className="flex-1 overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                  <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                    {/* Checkbox column */}
                    <TableHead className="h-12 font-semibold relative" style={{ width: `${columnWidths.select}px` }}>
                      <div className="flex items-center justify-center px-3">
                        <Checkbox
                          checked={paginatedData.length > 0 && paginatedData.every(item => selectedRowIds.has(item.id))}
                          onCheckedChange={handleSelectAll}
                        />
                      </div>
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                        <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                      </div>
                    </TableHead>
                    {orderedVisibleColumns.map((column) => {
                      const isRightAligned = ['purchaseValue', 'endOfYearNBV', 'insuranceValue'].includes(column.id);

                      return (
                        <TableHead 
                          key={column.id} 
                          className={`h-12 font-semibold relative ${isRightAligned ? 'text-right' : ''}`}
                          style={{ width: `${columnWidths[column.id]}px` }}
                        >
                          <Button
                            variant="ghost"
                            size="sm"
                            className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                            onClick={() => handleSort(column.id)}
                          >
                            {column.label}
                            <SortIcon columnKey={column.id} sortConfig={sortConfig} />
                          </Button>
                          {/* Resizable column divider */}
                          <div
                            className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-1 cursor-col-resize hover:bg-primary/20 group"
                            onMouseDown={(e) => handleMouseDown(column.id, e.clientX)}
                          >
                            <div className="absolute right-0 top-0 bottom-0 w-px bg-slate-300 dark:bg-slate-600 group-hover:bg-primary" />
                          </div>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={orderedVisibleColumns.length + 1}
                        className="h-96"
                      >
                        <div className="flex flex-col items-center justify-center space-y-4 py-12">
                          <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-6">
                            <FileX className="h-12 w-12 text-slate-400 dark:text-slate-500" />
                          </div>
                          <div className="space-y-2 text-center">
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">No assets found</h3>
                            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                              {searchQuery || Object.keys(tableFilters).length > 0
                                ? "Try adjusting your search or filters to find what you're looking for"
                                : "No assets available for insurance reporting"}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((item, index) => (
                      <TableRow 
                        key={item.id}
                        className={`
                          ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                          hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800
                        `}
                      >
                        {/* Checkbox cell */}
                        <TableCell style={{ width: `${columnWidths.select}px` }}>
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={selectedRowIds.has(item.id)}
                              onCheckedChange={(checked) => handleSelectionChange(item.id, checked as boolean)}
                            />
                          </div>
                        </TableCell>
                        {orderedVisibleColumns.map((column) => renderCellContent(column.id, item))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && sortedData.length > 0 && (
          <div className="border-t bg-card px-6 py-3 flex-shrink-0">
            <div className="flex flex-row flex-nowrap items-center justify-between gap-3 text-sm text-muted-foreground">
              <div className="flex-shrink-0">
                Showing {startIndex + 1}-{Math.min(endIndex, sortedData.length)} of {sortedData.length} entries
                {sortedData.length !== totalAssets && ` (filtered from ${totalAssets} total)`}
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {(() => {
                      const pageNumbers: (number | string)[] = [];
                      const maxVisiblePages = 7;
                      
                      if (totalPages <= maxVisiblePages) {
                        for (let i = 1; i <= totalPages; i++) {
                          pageNumbers.push(i);
                        }
                      } else {
                        pageNumbers.push(1);
                        
                        if (currentPage > 3) {
                          pageNumbers.push('...');
                        }
                        
                        const start = Math.max(2, currentPage - 1);
                        const end = Math.min(totalPages - 1, currentPage + 1);
                        
                        for (let i = start; i <= end; i++) {
                          pageNumbers.push(i);
                        }
                        
                        if (currentPage < totalPages - 2) {
                          pageNumbers.push('...');
                        }
                        
                        pageNumbers.push(totalPages);
                      }
                      
                      return pageNumbers.map((page, pageIndex) => {
                        if (page === '...') {
                          return (
                            <span key={`ellipsis-before-${pageNumbers[pageIndex + 1]}`} className="px-2 text-muted-foreground">
                              ...
                            </span>
                          );
                        }
                        
                        return (
                          <Button
                            key={`page-${page}`}
                            variant={currentPage === page ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setCurrentPage(page as number)}
                            className="min-w-[36px]"
                          >
                            {page}
                          </Button>
                        );
                      });
                    })()}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last
                  </Button>
                </div>
              )}
              
              <div className="flex-shrink-0 flex gap-4">
                <span>Total Purchase Value: {formatCurrency(totalPurchaseValue)}</span>
                <span>Total NBV: {formatCurrency(totalNBV)}</span>
                <span className="font-semibold">Total Insurance Value: {formatCurrency(totalInsuranceValue)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Page-specific Right Panel */}
      <RightPanelBase tabs={rightPanelTabs} defaultTab="actions" />
    </PageWithRightPanel>
  );
}

export default InsuranceValueReport;
